/**
 * queryOptions factories for YoY billing hooks.
 *
 * Extracting options into standalone factories (rather than inline in useQuery)
 * enables two things:
 *  1. Type-safe prefetching in YoYControls without calling a hook outside React
 *  2. Type-safe queryClient.getQueryData() — TypeScript infers the return type
 *     from the factory instead of returning `unknown`
 *
 * Each factory threads the AbortSignal from QueryFunctionContext into
 * makeApiRequest so React Query can cancel in-flight requests when the query
 * key changes (e.g. rapid professional filter clicks).
 */

import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import {
  dailyViewSchema,
  goLiveRecordArraySchema,
  professionalsSchema,
  yoySummarySchema,
  yoyTimeSeriesSchema,
} from "../types/schemas";
import type { RequestOptions, ApiResponse } from "@/shared/types/request";
import type { YoYGranularity } from "../types/dashboard";
import { TEST_ACCOUNTS } from "../utils/adminHelpers";

type MakeApiRequest = (options: RequestOptions) => Promise<ApiResponse>;

// ── YoY Summary ──────────────────────────────────────────────────────────────

export const yoySummaryOptions = (
  profId: string | null,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["yoy-summary", profId ?? "all"] as const,
    queryFn: async ({ signal }) => {
      const profParam = profId
        ? `?profId=${profId}&excludeInternal=true`
        : "?excludeInternal=true";
      const response = await makeApiRequest({
        endpoint: `/admin/billing/summary${profParam}`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      if (response.data == null) return null;
      return yoySummarySchema.parse(response.data);
    },
    placeholderData: keepPreviousData,
  });

// ── YoY Time Series ──────────────────────────────────────────────────────────

export const yoyTimeSeriesOptions = (
  granularity: YoYGranularity,
  profId: string | null,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["yoy-timeseries", granularity, profId ?? "all"] as const,
    // The breakdown endpoint only supports weekly/monthly — daily uses useDailyView instead
    enabled: granularity !== "daily",
    queryFn: async ({ signal }) => {
      const profParam = profId ? `&profId=${profId}` : "";
      const response = await makeApiRequest({
        endpoint: `/admin/billing/breakdown?granularity=${granularity}${profParam}&excludeInternal=true`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      return yoyTimeSeriesSchema.parse(response.data?.rows ?? []);
    },
    placeholderData: keepPreviousData,
  });

// ── Daily View ───────────────────────────────────────────────────────────────

export const dailyViewOptions = (
  profId: string | null,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["daily-view", profId ?? "none"] as const,
    // enabled: profId !== null — query only runs when a specific attorney is selected
    enabled: profId !== null,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: `/admin/billing/daily?profId=${profId!}`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      if (response.data == null) return null;
      return dailyViewSchema.parse(response.data);
    },
    placeholderData: keepPreviousData,
  });

// ── Go-Live Dates ─────────────────────────────────────────────────────────────

export const goLiveDatesOptions = (makeApiRequest: MakeApiRequest) =>
  queryOptions({
    queryKey: ["go-live-dates"] as const,
    queryFn: async ({ signal }) => {
      const response = await makeApiRequest({
        endpoint: "/admin/billing/go-live-dates",
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      return goLiveRecordArraySchema.parse(response.data ?? []);
    },
  });

// ── Professionals ─────────────────────────────────────────────────────────────

export const professionalsOptions = (
  activeOnly: boolean,
  makeApiRequest: MakeApiRequest
) =>
  queryOptions({
    queryKey: ["professionals", activeOnly] as const,
    queryFn: async ({ signal }) => {
      const params = activeOnly
        ? "?activeOnly=true&excludeInternal=true"
        : "?excludeInternal=true";
      const response = await makeApiRequest({
        endpoint: `/admin/professionals${params}`,
        method: "GET",
        otherOptions: { signal },
      });
      if (response.error) throw new Error(response.error);
      const professionals = professionalsSchema.parse(response.data ?? []);
      return professionals.filter((p) => !TEST_ACCOUNTS.includes(p.full_name));
    },
  });
